import { ReferenceElement } from "@floating-ui/react";
import { DependencyList, MutableRefObject, useEffect, useState } from "react";

export { useScopedColorScheme };

/**
 * Get the data-scheme value of the closest parent with a data-scheme attribute.
 * Useful for components that render elements in portals.
 * @see {@link https://react.dev/reference/react-dom/createPortal}
 *
 * Probably not the most optimal solution. A simple prop would be better?
 *
 * @param ref The element to check for a data-scheme attribute, or its closest parent with one.
 * @param deps A list of dependencies to trigger a check when changed.
 * @returns The data-scheme value or null.
 */
const useScopedColorScheme = (
  ref: MutableRefObject<HTMLElement | ReferenceElement | null>,
  deps: DependencyList = []
) => {
  const [scopedScheme, setScopedScheme] = useState<string | null>(null);
  const [scopedMode, setScopedMode] = useState<string | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const el: HTMLElement | null = (ref.current as HTMLElement).closest(
      "[data-scheme]"
    );
    const scope = el ? el.dataset.scheme || null : null;
    if (scopedScheme !== scope) {
      setScopedScheme(scope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, scopedScheme, ...deps]);

  useEffect(() => {
    if (!ref.current) return;
    const el: HTMLElement | null = (ref.current as HTMLElement).closest(
      "[data-mode]"
    );
    // If data-mode is set in <html>, we don't need to pass it around.
    if (el === document.documentElement) return;
    const mode = el ? el.dataset.mode || null : null;
    if (scopedMode !== mode) {
      setScopedMode(mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, scopedMode, ...deps]);

  return {
    colorScheme: scopedScheme,
    colorMode: scopedMode,
  };
};
